"use client";

import {useState} from "react";
import {FaqProps} from "@/shared/pages/faqShared";
import {consts, icons} from "@/shared/commonShared";
import ExportedImage from "next-image-export-optimizer";

export default function Faq({ initialFaqs }: FaqProps) {
    const [activeItem, setActiveItem] = useState<number | null>(1);

    const handleActiveItem = (index: number) => {
        setActiveItem(prev => (prev === index ? null : index));
    };
    if (!initialFaqs || !initialFaqs.length) {
        return null;
    }

    return (
        <section className={"section-faq position-relative py-"+consts.PADDING}>
            <div className="container position-relative z-2">
                <div className="row align-items-center">
                    {/* Left: image + 3 feature bullets */}
                    <div className="col-lg-6">
                        <div className="text-start position-relative d-inline-block mb-lg-0 w-100">
                            {/* main image */}
                            <ExportedImage
                                className="w-100 rounded-4 img-fluid"
                                src="/assets/imgs/new/faq-2.png"
                                alt="FAQs illustration"
                                width={580}
                                height={780}
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />

                            <div className={"px-3 my-"+consts.PADDING+" card-team rounded-4 z-1 backdrop-filter w-100 p-3 p-lg-5 py-lg-8 m-lg-5 position-lg-absolute bottom-lg-0 start-lg-0 end-lg-0 w-auto"}>
                                {/* item 1 */}
                                <div
                                    className="d-flex flex-column flex-md-row align-items-start gap-3 mb-4"
                                    data-aos="fade-zoom-in"
                                    data-aos-delay={0}
                                >
                                    <ExportedImage
                                        src={icons["inHouse"]}
                                        alt="In-house production"
                                        width={48}
                                        height={48}
                                    />
                                    <div>
                                        <h6 className="m-0">100% In-House Production</h6>
                                        <p className="m-0">
                                            From design to launch, every aspect of your project is handled by our core team.
                                        </p>
                                    </div>
                                </div>

                                {/* item 2 */}
                                <div
                                    className="d-flex flex-column flex-md-row align-items-start gap-3 mb-4"
                                    data-aos="fade-zoom-in"
                                    data-aos-delay={100}
                                >
                                    <ExportedImage
                                        src={icons["fast"]}
                                        alt="Fast delivery"
                                        width={48}
                                        height={48}
                                    />
                                    <div>
                                        <h6 className="m-0">Fast And Flexible Delivery</h6>
                                        <p className="m-0">
                                            We adapt to your timeline - both for full projects or urgent help mid-production.
                                        </p>
                                    </div>
                                </div>

                                {/* item 3 */}
                                <div
                                    className="d-flex flex-column flex-md-row align-items-start gap-3"
                                    data-aos="fade-zoom-in"
                                    data-aos-delay={200}
                                >
                                    <ExportedImage
                                        src={icons["experts"]}
                                        alt="Expert-led team"
                                        width={48}
                                        height={48}
                                    />
                                    <div>
                                        <h6 className="m-0">Expert-Led Creative Team</h6>
                                        <p className="m-0">
                                            Our leads come from the companies like Disney, Ubisoft and more - with decades of combined experience.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: accordion */}
                    <div className={"col-lg-6 mt-lg-0 mt-"+consts.PADDING}>
                        <h3 className="ds-3" data-aos="fade-up" data-aos-delay={0}>
                            Frequently Asked Questions
                        </h3>
                        <p className={"fs-5 my-"+consts.PADDING_SMALL} data-aos="fade-up" data-aos-delay={0}>
                            Find the answers to all of our most frequently asked questions
                        </p>

                        <div className="accordion">
                            {initialFaqs.map((faq) => {
                                const isActive = activeItem === faq.id;
                                return (
                                    <div
                                        className="px-0 card border-0 border-bottom bg-transparent rounded-0"
                                        data-aos="fade-up"
                                        data-aos-delay={0}
                                        key={faq.id}
                                    >
                                        <div className="px-0 card-header border-0 bg-gradient-1 pointer">
                                            <button
                                                type="button"
                                                className={`${
                                                    isActive ? "accordion-button" : "accordion-button collapsed"
                                                } text-900 fw-bold d-flex align-items-center`}
                                                onClick={() => handleActiveItem(faq.id)}
                                                aria-expanded={isActive}
                                                aria-controls={`collapse-${faq.id}`}
                                            >
                        <span className="icon-shape icon-xs fs-7 rounded-circle d-none d-md-block me-3 bg-primary text-white">
                          {faq.id}
                        </span>
                                                <h6 className="m-0">{faq.question}</h6>
                                            </button>
                                        </div>
                                        <div
                                            id={`collapse-${faq.id}`}
                                            className={
                                                isActive
                                                    ? "accordion-collapse collapse show"
                                                    : "accordion-collapse collapse"
                                            }
                                            data-bs-parent=".accordion"
                                        >
                                            <p className={"px-0 card-body fs-6 text-600 py-"+consts.PADDING_SMALL}>{faq.answer}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* background decorative image */}
            <div className="position-absolute top-0 start-50 translate-middle-x z-0">
                <ExportedImage
                    src="/assets/imgs/blog/img-bg-line.png"
                    alt="background line"
                    width={828}
                    height={200}
                    priority={false}
                />
            </div>
        </section>
    );
}
